// ─── Products module — domain dictionary ───────────────────────────────────
//
// A therapy Product is the DIN-tracked output of a manufacturing/collection
// step, tied to one patient — distinct from Inventory's `Material`
// (consumables/reagents/kits, see src/types/inventory.ts). The product-code
// CATALOG below (`ProductCatalogEntry`) is the ISBT 128 standard's 14,911
// codes, shown on Configure → Products; a `CreatedProduct` is one DIN-minted
// instance of a catalog entry, created from a patient's form.

import type { StorageCondition } from '@/types/storage'

// ─── ISBT 128 product codes ─────────────────────────────────────────────────
//
// The catalog is GLOBAL reference data, not a tenant's list: ISBT 128 is a
// published standard, so all 14,911 codes are seeded once and every tenant
// reads the same rows. The route is still tenant-guarded for the permission
// check, which is why the API client still passes a tenant id.

/**
 * The 40 free-text columns parsed out of an ISBT 128 product description.
 *
 * Grouped the way the standard's description string is: segment 1 is the
 * product, segment 2 the always-three-part triplet, and everything after that
 * an attribute. Most are blank on most rows — `hematocrit` is set on 49 of the
 * 14,911 codes — so treat '' as "not stated", never as missing data.
 */
export interface IsbtParsedFields {
  // Segment 1 — the product itself.
  productClass: string
  sourceTissue: string
  collectionModifier: string
  stateModifier: string

  // Segment 2 — Anticoagulant / Collection volume / Storage temperature.
  anticoagulant: string
  /** Blank wherever the source held the `XX` placeholder (13,106 of 14,911). */
  collectionVolume: string
  storageTemperature: string

  // Segments 3+ — bare attribute terms, placed into a category column.
  containers: string
  containerAutomation: string
  unitCount: string
  donorCount: string
  volume: string
  plateletContent: string
  hematocrit: string
  cryoprotectant: string
  additiveAdded: string
  componentReduction: string
  cellSelection: string
  pathogenReduction: string
  irradiation: string
  preservationState: string
  processing: string
  manipulationExtent: string
  collectionDetail: string
  microbialTesting: string
  intendedUse: string
  antigenSpecificity: string

  // Segments 3+ — Key:Value attributes, carrying the value only.
  residualLeukocytes: string
  otherAdditives: string
  thirdPartyComponents: string
  forManufacturing: string
  quarantine: string
  cultured: string
  finalContainer: string
  geneticallyModified: string
  pooledSingleDonor: string
  lowVolume: string
  granulocytesPrep: string

  /** Any term the parser could not place. Empty on every row of the current file. */
  unparsedAttributes: string
}

/** One ISBT 128 product code — a row of the master list (Configure → Products). */
export interface ProductCatalogEntry extends IsbtParsedFields {
  id: string
  /** ISBT 128 product description code, e.g. "S2487". The natural key. */
  productDescriptionCode: string
  /**
   * The original HL7 string, stored verbatim.
   *
   * The parsed columns are derived from this and were not correct on every row,
   * so this is both the fallback when a column looks wrong and the reason
   * search covers it.
   */
  description: string
  inUse: boolean
  expired: boolean
  /** Number of attribute segments in the original description (0-9). */
  attributeCount: number
  /**
   * The storage condition the SELECTED TENANT maps this code's
   * `storageTemperature` to (Configure → Products → Storage Temp Mapping).
   * null when the temperature is blank or not mapped yet — then any unit is
   * allowed. The server resolves it per request; it is not stored on the code.
   */
  storageCondition: StorageCondition | null
  createdAt: string
  updatedAt: string
}

/**
 * A catalog row as the server returns it: the global ISBT entry plus the
 * SELECTED TENANT's own status on it (z8nrz7e72t).
 *
 * `active` and `note` are the only two things a user can change about a code.
 * They live in the backend's `ProductCodeStatus`, per tenant, because the
 * catalog itself is shared by every tenant. A code nobody in the tenant has
 * decided on reports `inUse && !expired` and an empty note.
 */
export interface ProductCatalogRow extends ProductCatalogEntry {
  active: boolean
  note: string
  /** When and by whom the tenant last set Active or Note. Null until someone does. */
  statusUpdatedAt: string | null
  statusUpdatedBy: string | null
}

/** The catalog's Active / Inactive filter. `undefined` on the wire means both. */
export const CATALOG_STATUSES = ['active', 'inactive'] as const
export type CatalogStatus = (typeof CATALOG_STATUSES)[number]

/** The Excel upload's answer: how many codes were written, and the codes it did not recognise. */
export interface CatalogStatusImportResult {
  updated: number
  unknownCodes: string[]
}

/**
 * The columns a caller may filter the catalog by — the summary table's columns
 * minus the code and the description, which `search` covers.
 *
 * Must stay in step with `CATALOG_FILTER_FIELDS` in the backend's
 * `list-catalog.dto.ts`: a key here the server does not know is silently
 * dropped, leaving a dropdown that appears to do nothing.
 */
export const CATALOG_FILTER_FIELDS = [
  'productClass',
  'sourceTissue',
  'anticoagulant',
  'storageTemperature',
  'cryoprotectant',
  'preservationState',
  'processing',
  'thirdPartyComponents',
  'geneticallyModified',
] as const

export type CatalogFilterField = (typeof CATALOG_FILTER_FIELDS)[number]

/** Filters offered on the product-selection popup (all optional/AND'd). */
export type ProductCatalogFilters = Partial<Record<CatalogFilterField, string>> & {
  /** Matched against the product code AND the raw HL7 description. */
  search?: string
  /**
   * The "added when" window, as absolute instants.
   *
   * Absolute rather than "last N days" because the presets are not all
   * durations — "this month" and "this year" are calendar boundaries, and a
   * custom range has two ends. Computed from the operator's local timezone by
   * `src/lib/dateRangePresets.ts`.
   */
  createdFrom?: string
  createdTo?: string
  /** Hide the 1,492 expired codes. */
  inUseOnly?: boolean
  /** The selected tenant's Active / Inactive. Omitted means both. */
  status?: CatalogStatus
  page?: number
  pageSize?: number
  /** Column sort, `<column>:<asc|desc>`, applied server-side before paging (z8nrz7e4h6). */
  sort?: string
}

/** One page of the catalog. The table holds 14,911 rows; it is never sent whole. */
export interface ProductCatalogPage {
  items: ProductCatalogRow[]
  total: number
  page: number
  pageSize: number
}

/** Distinct values per filterable column, for the picker's dropdowns. */
export type ProductCatalogFacets = Record<CatalogFilterField, string[]>

/** What a bulk import did. Idempotent on the code, so re-importing refreshes. */
export interface CatalogImportResult {
  created: number
  updated: number
  total: number
}

/**
 * One distinct catalog `storageTemperature` and the condition this tenant maps
 * it to. Spellings that differ only in case or spacing (`refg`, ` Refg `) are
 * one row: `temperatureKey` is the normalised form the mapping is stored under.
 */
export interface StorageTempMappingRow {
  /** A catalog spelling — the most common one for the key. */
  storageTemperature: string
  temperatureKey: string
  /** Catalog codes carrying this temperature. */
  productCount: number
  condition: StorageCondition | null
  /** The server's reading of the temperature, offered while `condition` is null. */
  suggestedCondition: StorageCondition | null
  updatedBy: string | null
  updatedAt: string | null
}

export interface StorageTempMappingList {
  rows: StorageTempMappingRow[]
  /** Rows with no condition — the toolbar badge. */
  unmapped: number
}

/** One mapping to write. `condition: null` clears it. */
export interface StorageTempMappingChange {
  storageTemperature: string
  condition: StorageCondition | null
}

/** One DIN-minted product, created from a catalog entry for a patient submission. */
export interface CreatedProduct {
  id: string
  din: string
  /**
   * The ISBT 128 flag printed after the DIN (z8nrz7fxwh). Two digits, not part
   * of the DIN. Null on products made before flags, and absent from an older
   * backend. The check character is derived from the DIN (`dinCheckCharacter`).
   */
  dinFlag?: string | null
  productDescriptionCode: string
  catalogEntryId: string
  /**
   * The catalog entry's ISBT description, raw ("HPC, APHERESIS|Citrate/XX/rt|…").
   * Read it with `catalogName`. Absent from an older backend.
   */
  catalogDescription?: string | null
  submissionId: string
  formDefinitionId: string
  /**
   * The form this product was made in (server-joined): its name, and which
   * repeat of a repeating step (null when the step does not repeat). A split
   * aliquot's is the form the split was performed in. Null when the form no
   * longer resolves; absent from an older backend.
   */
  associatedForm?: { name: string; repeatIndex: number | null } | null
  procedureId?: string | null
  /** Omitted when the owning form's `requiresPatientHistory` is false (e.g. kits). */
  patientMrn?: string | null
  patientName?: string | null
  patientDob?: string | null
  /** The POSITION this product is held in, assigned after Print Label. */
  storageNodeId?: string | null
  /** That position's materialised path (server-joined), e.g. "FRZ-01/R1/B1/A1". */
  storagePath?: string | null
  /**
   * The partner a shipped product was sent to (z8nrz7eaex). Its location is
   * then the partner's code. Mutually exclusive with `storageNodeId`.
   */
  locationPartnerId?: string | null
  locationPartner?: { id: string; code: string; name: string } | null
  /** Set on an aliquot created by Split Product — the product it was split from. */
  parentProductId?: string | null
  /** This product's own volume, in mL. Recorded at Split; unset for products made by Create/Receive. */
  volumeMl?: number | null
  /** Scaled LIMS totals recorded at Split (TNC, CD34, CD3, RBC volume, …), keyed by the source form's field id. */
  limsValues?: Record<string, number> | null
  createdAt: string
  createdBy: string
  /** Label choices from the last print — drives an accurate reprint. */
  labelSelections?: StoredLabelSelections | null
  /**
   * Set once this product's code was CHANGED (z8nrz7e72t): the id of the
   * product that replaced it. A superseded product is history — kept, shown
   * greyed, and refused by every action; the bag lives on as the new one.
   */
  supersededById?: string | null
  supersededAt?: string | null
}

/** The label choices from the last print, kept so a reprint reproduces it. */
export interface StoredLabelSelections {
  labelType: string
  bloodType?: string | null
  specialMessage?: string | null
  intendedUse?: string | null
  donationType?: string | null
  freeText?: string[]
}

// A product's audit trail is no longer its own shape: it is served in the
// unified `AuditTrailRow` (see src/types/audit.ts), the same rows the panel
// under a form shows, so both render through one table.

/**
 * Why a recorded volume changed. Fixed list for v1 — the ticket left open
 * whether this should become an admin-managed pick list; "Other" plus free
 * text keeps that from blocking anyone in the meantime.
 */
export const PRODUCT_VOLUME_REASONS = [
  'Sampling',
  'QC',
  'Transfer loss',
  'Correction',
  'Other',
] as const
export type ProductVolumeReason = (typeof PRODUCT_VOLUME_REASONS)[number]

// ─── Split preview ──────────────────────────────────────────────────────────
//
// What a split WOULD do to a product's lab results, answered by the server
// (`POST /products/:id/split/preview`) before anything is created. The dialog
// shows an operator each aliquot's before/after figures and the formula that
// produced them.
//
// It is a server answer rather than a client calculation on purpose: these are
// dosing figures, and a preview worked out here could disagree with the one
// the split then stores. The formulas live in one place —
// `cgt-backend/src/products/lims-calculations.ts` — and both the preview and
// the stored result come out of the same pass. ClickUp z8nrz7dkvf.

/** A LIMS result key with its label, as the server names it. */
export interface LimsResultRef {
  key: string
  label: string
}

/** One result on one proposed aliquot. */
export interface SplitPreviewRow extends LimsResultRef {
  /** The source product's own figure. Null when it carries none for this key. */
  before: number | null
  /** What the aliquot would be given. */
  after: number
  /** The rule in named terms, e.g. "source TNC/mL × aliquot cell volume". */
  formula: string
  /** The same rule with this aliquot's numbers in it, e.g. "6.04 × 42 = 253.68". */
  substituted: string
}

// ─── Pool preview ───────────────────────────────────────────────────────────
//
// What pooling a selection WOULD produce, answered by the server
// (`POST /products/pool/preview`) before anything is created. The mirror of
// the split preview below, and a server answer for the same reason: these are
// dosing figures, and a preview worked out here could disagree with the one
// the pool then stores. Formulas live in
// `cgt-backend/src/products/pool-calculations.ts`. ClickUp z8nrz7e32b.

/** Which of the three sources a member's dose was resolved from. */
export type PoolDoseRule = 'direct' | 'single' | 'dual'

/** Something the operator has to be told about a pool. */
export type PoolWarningCode =
  | 'zero_tnc'
  | 'no_member_volumes'
  | 'no_measurements'
  | 'single_platform_lost'
  | 'direct_entry_override'

export interface PoolWarning {
  code: PoolWarningCode
  message: string
}

/** One result on the pooled product. */
export interface PoolPreviewRow extends LimsResultRef {
  after: number
  formula: string
  substituted: string
}

/** What one member contributes to the pool. */
export interface PoolPreviewMember {
  label: string | null
  volumeMl: number | null
  tncTotal: number | null
  cd34Total: number | null
  cd3Total: number | null
  rbcVolumeMl: number | null
  /** Which rule produced this member's dose. Null when it carries none. */
  cd34Rule: PoolDoseRule | null
  cd3Rule: PoolDoseRule | null
  measured: (LimsResultRef & { value: number })[]
}

/**
 * One dose input a split or pool could not find, as the server reports it.
 *
 * Inputs resolve by the Field Library SYSTEM NAME of the form field holding
 * them (`tnc_per_ml`, …), on the current form or an earlier one in the
 * procedure. `not_on_form`: no form carries that field. `no_value`: a form
 * does, but it is empty.
 */
export interface MissingDoseInputOption {
  systemName: string
  label: string
  /** `ambiguous`: held in two places on one form with different values. */
  reason: 'not_on_form' | 'no_value' | 'ambiguous'
  /** `ambiguous` only — each place and its value, as the builder names it. */
  places?: string[]
}

/** A requirement nothing satisfied — any ONE of `anyOf` would have. */
export interface MissingDoseInput {
  anyOf: MissingDoseInputOption[]
  /** `DIN …` — which member, on a pool. */
  product?: string
}

/** Why nothing can be previewed. Separate reasons, separate fixes. */
export type PoolPreviewUnavailable = 'too_few_members' | 'no_member_volumes' | 'no_measurements'

export interface PoolPreview {
  /** Σ member volumes + buffer — what the pooled bag will hold. */
  volumeMl: number
  bufferMl: number
  /** Resolved exactly as the pool itself will resolve it. Null when none is on file. */
  patientWeightKg: number | null
  unavailable: PoolPreviewUnavailable | null
  warnings: PoolWarning[]
  members: PoolPreviewMember[]
  rows: PoolPreviewRow[]
  /** Per-kg doses absent only for want of a recipient weight. */
  missingForWeight: LimsResultRef[]
  /** What the pool would be refused over. Non-empty blocks the pool. */
  inputsNotFound: MissingDoseInput[]
}

export interface SplitPreviewChild {
  /** Echoed back from the request, so rows line up with the dialog's own table. */
  productDescriptionCode: string | null
  volumeMl: number | null
  /** The volume actually carrying cells — the figure every formula uses. */
  cellVolumeMl: number | null
  rows: SplitPreviewRow[]
}

/** A measurement the source product does not carry, and the results it costs. */
export interface SplitPreviewMissingInput extends LimsResultRef {
  blocks: LimsResultRef[]
}

/**
 * Why nothing at all can be previewed. Separate reasons because they have
 * separate fixes — `no_measurements` is the case of a product the LIMS never
 * reported on, which includes everything created before the results existed.
 */
export type SplitPreviewUnavailable =
  | 'no_source_volume'
  | 'no_measurements'
  | 'no_aliquot_volumes'

export interface SplitPreview {
  source: {
    volumeMl: number | null
    cellVolumeMl: number | null
    /** `'form'` when read off the procedure's forms; `'mock'` on old products. */
    resultsSource: string | null
    measured: (LimsResultRef & { value: number })[]
  }
  /** The share of a product volume assumed to be cryomedia rather than cells. */
  cryomediaFraction: number
  /** Resolved exactly as the split itself will resolve it. Null when none is on file. */
  patientWeightKg: number | null
  unavailable: SplitPreviewUnavailable | null
  missingMeasured: SplitPreviewMissingInput[]
  /** Per-kg doses absent only for want of a recipient weight. */
  missingForWeight: LimsResultRef[]
  children: SplitPreviewChild[]
  /**
   * Inputs nothing supplied, neither a form nor a typed value. The split still
   * goes ahead; the figures that need them are left out and the audit says so.
   */
  inputsNotFound: MissingDoseInput[]
  /** The typed values the server accepted (gaps only). */
  manualInputs?: Record<string, number>
}

/** A product of the same split, as the product page lists it. */
export interface ProductRelative {
  id: string
  din: string
  productDescriptionCode: string
  /** The division suffix ("A0", "Ab"), or null for an undivided product. */
  division: string | null
  volumeMl: number | null
  superseded: boolean
  storagePath: string | null
  locationPartner: { code: string; name: string } | null
}

/** `GET /products/:id/record` — the product page's record (z8nrz7g57f). */
export interface ProductRecord {
  /** The ISBT 128 catalog attributes. Text fields are null where the code does not state them. */
  catalog: {
    productDescriptionCode: string
    description: string | null
    productClass: string | null
    sourceTissue: string | null
    anticoagulant: string | null
    cryoprotectant: string | null
    preservationState: string | null
    storageTemperature: string | null
    containers: string | null
    finalContainer: string | null
    intendedUse: string | null
    collectionDetail: string | null
    processing: string | null
    quarantine: string | null
  }
  division: string | null
  parent: { id: string; din: string; productDescriptionCode: string } | null
  /** Every product split from the same parent, this one included. */
  siblings: ProductRelative[]
  /** The products split from this one. */
  children: ProductRelative[]
  procedure: {
    id: string
    name: string
    type: string
    status: string
    startedAt: string
    patientMrn: string | null
  } | null
  /** From the run's product release review form; 'none' when it has none. */
  release: 'released' | 'awaiting' | 'none'
  /** When it was last placed in storage. */
  storedAt: string | null
  /** The recipient weight its per-kg figures use, from the run's forms. */
  recipientWeightKg: number | null
  storage: {
    unit: { code: string; name: string | null; equipmentLabel: string | null; equipmentType: string | null } | null
    condition: { code: string; label: string; temperature: string | null } | null
  } | null
}
